import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { nanoid } from 'nanoid'
import { uiStore } from 'shared/store/uiStore'
import type { ICardFilter } from 'shared/ui'
import { CalendarFilterStore } from 'shared/ui/CalendarDropdown/store/CalendarFilterStore'
import { TableStore } from 'shared/ui/TableNew'
import { secondsToTime } from 'shared/lib/secondsToTime'
import { RequestLoadData } from 'shared/api/lib'
import { numberFormat } from 'shared/lib'
import {
  type IParamsGetAgentCals,
  type IParamsGetAgentCalsStatistics,
  type IResponseFullAICallingAgent,
  type IResponseGetAgentCall,
  type IResponseGetAgentCalsStatistics,
} from 'entities/AICalling/api/types'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { AiCallingApi } from 'entities/AICalling/api/aiCallingApi'
import type { IOpenContactsDetails } from 'widgets/ContactsDetails'
import { ContactsDetailsManageStore } from 'widgets/ContactsDetails/store'
import { ChatbotPath } from 'pages/chatbot/route/chatbotPath'
import { CallingContactListItem } from 'pages/chatbot/pages/calling/pages/view/ui/StatisticsTable/store/CallingContactListItem'
import { CallingAgentIdStore } from 'pages/chatbot/pages/calling/store/CallingAgentIdStore'
import { AgentKnowledgeBaseStore } from 'pages/chatbot/ui/AgentKnowledgeBase/store/AgentKnowledgeBaseStore.ts'

export class ViewCallingStore {
  readonly callingAgentIdStore
  readonly calendarFilterStore
  readonly tableStore
  private _contactsDetailsManageStore: ContactsDetailsManageStore

  private _disposeParamsWithoutPage: IReactionDisposer | null = null
  private _disposeParamsGetItems: IReactionDisposer | null = null

  private _search = ''
  private _itemsMap: Map<string, IResponseGetAgentCall> = new Map()
  private _initialLoading = false
  private _statistics: IResponseGetAgentCalsStatistics | null = null
  readonly agentKnowledgeBaseStore = new AgentKnowledgeBaseStore()

  constructor(readonly entities: IEntitiesStore, readonly id: number) {
    this.calendarFilterStore = new CalendarFilterStore(null)
    this.callingAgentIdStore = new CallingAgentIdStore(id, {
      onError: this._onErrorSetAgent,
      onSetAgent: this._onSetAgent,
    })
    this.tableStore = new TableStore<CallingContactListItem>({
      key: 'detailCallingAiAgentTable',
      limit: 10,
      page: 1,
      useColumnsCache: true,
    })
    this._contactsDetailsManageStore = new ContactsDetailsManageStore(this.entities)
    makeAutoObservable(this)
  }

  init = () => {
    this.calendarFilterStore.setFromFilterDefaultDate()
    this._contactsDetailsManageStore.init()

    this.loadData()

    this.reactionParamsWithoutPage()
    this.reactionParamsGetItems()
    return this.dispose
  }

  dispose = () => {
    this._disposeParamsWithoutPage?.()
    this._disposeParamsGetItems?.()
    this._contactsDetailsManageStore.reset()
    this._requestLoadDataCals.dispose()
    this._requestLoadCalsStatistics.dispose()
  }

  reactionParamsWithoutPage = () => {
    this._disposeParamsWithoutPage?.()
    this._disposeParamsWithoutPage = reaction(
      () => this.paramsWithoutPage,
      () => {
        this.tableStore.setLoading(true)
        this.tableStore.setPage(1)
      }
    )
  }

  reactionParamsGetItems = () => {
    this._disposeParamsGetItems?.()
    this._disposeParamsGetItems = reaction(
      () => this.paramsGetItems,
      () => {
        this._requestLoadDataCals.debounceLoadData()
        this._requestLoadCalsStatistics.debounceLoadData()
      }
    )
  }

  get paramsOutcome(): IParamsGetAgentCals['outcome'] {
    if (this.activeFilter === 'total' || this.activeFilter === 'duration') {
      return undefined
    }
    return this.activeFilter
  }
  get paramsWithoutPage(): Omit<IParamsGetAgentCals | IParamsGetAgentCalsStatistics, 'page'> {
    return {
      limit: this.tableStore.limit || 0,
      search: this.search || undefined,
      outcome: this.paramsOutcome,
      filtersList: this.calendarFilterStore.sendDate
        ? [
            {
              filters: [
                {
                  key: 'call.date',
                  operator: 'is_between',
                  value: [
                    this.calendarFilterStore.sendDate?.from?.toISOString() || '',
                    this.calendarFilterStore.sendDate?.to?.toISOString() || '',
                  ],
                },
              ],
            },
          ]
        : undefined,
    }
  }

  get paramsGetItems(): IParamsGetAgentCals | IParamsGetAgentCalsStatistics {
    return {
      ...this.paramsWithoutPage,
      page: this.tableStore.page,
    }
  }

  loadData = () => {
    this._loadDataCals()
    this._loadDataCalsStatistics()
  }

  private _onSetAgent = (agent: IResponseFullAICallingAgent) => {
    if (agent.knowledge_base_enabled) {
      this.agentKnowledgeBaseStore.initKnowledgeBase().then(() => {
        this.agentKnowledgeBaseStore.agentDocumentsStore.init(agent.knowledge_base_documents || [])
      })
    }

    this.calendarFilterStore.updateCreatedAt(agent?.created_at)
    this.calendarFilterStore.setFromFilterDefaultDate()
  }

  private _onErrorSetAgent = () => {
    uiStore.changeRoute({
      path: ChatbotPath.calling,
    })
  }

  get agent() {
    return this.callingAgentIdStore.agent
  }

  onSuccessDelete = () => {
    uiStore.changeRoute({
      path: ChatbotPath.calling,
    })
  }

  onSuccessUpdate = (agent: IResponseFullAICallingAgent) => {
    this.callingAgentIdStore.syncOrigin(agent)
  }

  get filters(): ICardFilter<'total' | IParamsGetAgentCals['outcome'] | 'duration'>[] {
    return [
      {
        key: 'total',
        amount: this._statistics?.total_calls,
        title: 'Total calls',
        tooltipProps: {
          label: 'Calls received by this agent',
          placement: 'top',
        },
      },
      {
        key: 'resolved',
        amountContent: numberFormat({
          value: this._statistics?.resolved_calls || 0,
        }),
        percentage: Number((this._statistics?.resolved_percentage || 0).toFixed(1)),
        title: 'Resolved',
        tooltipProps: {
          label: 'Contacts helped by the agent without escalation',
          placement: 'top',
        },
      },
      {
        key: 'transferred',
        amountContent: numberFormat({
          value: this._statistics?.transferred_calls || 0,
        }),
        percentage: Number((this._statistics?.transferred_percentage || 0).toFixed(1)),
        title: 'Handed off',
        tooltipProps: {
          label: 'Contacts handed off to human',
          placement: 'top',
        },
      },
      {
        key: 'abandoned',
        amountContent: numberFormat({
          value: this._statistics?.abandoned_calls || 0,
        }),
        percentage: Number((this._statistics?.abandoned_percentage || 0).toFixed(1)),
        title: 'Abandoned',
        tooltipProps: {
          label: 'Contacts who ended the call before it was resolved',
          placement: 'top',
        },
      },
      {
        key: 'duration',
        amountContent: secondsToTime(this._statistics?.average_duration_seconds || 0, true),
        title: 'Average duration',
        notInteractable: true,
        tooltipProps: {
          label: 'Average length of a call handled by this agent',
          placement: 'top',
        },
      },
    ]
  }

  get search() {
    return this._search
  }

  setSearch = (value: string) => {
    this._search = value
  }

  get isEmpty() {
    if (this.tableStore.loading) return false
    if (this.search) return false

    return !this.itemsRows.length
  }

  get items() {
    return Array.from(this._itemsMap.values())
  }

  get itemsRows() {
    return this.items.map((item) => new CallingContactListItem(item, this.entities))
  }

  get initialLoading() {
    return this._initialLoading
  }

  private _requestLoadDataCals = new RequestLoadData({
    requestCallback: async ({ cancelRequest }) => {
      this.tableStore.setLoading(true)
      const { data } = await AiCallingApi.getAgentCalls(this.id, this.paramsGetItems, {
        cancelToken: cancelRequest.token,
      })

      runInAction(() => {
        this._itemsMap.clear()
        data.data.forEach((item) => {
          const id = nanoid()
          this._itemsMap.set(id, { ...item, id })
        })
        this.tableStore.setTotal(data.meta.total)
      })

      this.tableStore.setLoading(false)
    },
    finallyCallback: () => {
      runInAction(() => {
        this._initialLoading = false
      })
    },
  })

  private _loadDataCals = async () => {
    return this._requestLoadDataCals.loadData()
  }

  get statistics() {
    return this._statistics
  }

  private _requestLoadCalsStatistics = new RequestLoadData({
    requestCallback: async ({ cancelRequest }) => {
      this.tableStore.setLoading(true)

      const { data } = await AiCallingApi.getAgentCallsStatistics(this.id, this.paramsGetItems, {
        cancelToken: cancelRequest.token,
      })

      runInAction(() => {
        this._statistics = data
      })
    },
  })

  private _loadDataCalsStatistics = async () => {
    return this._requestLoadCalsStatistics.loadData()
  }

  get onOpenContactsDetails(): IOpenContactsDetails {
    return {
      disabled: this._contactsDetailsManageStore.disabled,
      open: async (data) => {
        await this.entities.inboxesStore.fetchInboxes()
        const inboxId = data.inboxId && this.entities.inboxesStore.getItem(data.inboxId)?.id
        this._contactsDetailsManageStore.onOpenContactDetails(
          {
            contactId: data.contactId,
            inboxId,
          },
          'aiCallingAgent',
          true
        )
      },
    }
  }

  get contactsDetailsStore() {
    return this._contactsDetailsManageStore.contactsDetailsStore
  }

  private _activeFilter: (typeof this.filters)[number]['key'] = 'total'
  get activeFilter() {
    return this._activeFilter
  }
  setActiveFilter = (filter: typeof this._activeFilter) => {
    this._activeFilter = filter
  }
}
